import { useMemo, useState } from "react";
import styles from "./MobileDeviceTable.module.scss";
import Modal from "../modal/modal";
import DeactiveDeviceModal from "../modals/DeactiveDeviceModal/DeactiveDeviceModal";
import GetPremiumModal from "../modals/GetPremiumModal/GetPremiumModal";
import OrderSummaryModal from "../modals/OrderSummaryModal/OrderSummaryModal";
import RenewalModal from "../modals/RenewalModal/RenewalModal";
import ProcessingModal from "../modals/ProcessingModal/ProcessingModal";
import SuccessfullyRenewalModal from "../modals/SuccessfullyRenewalModal/SuccessfullyRenewalModal";
import { useNavigate } from "react-router-dom";
import { RoutePaths } from "../../routes/RoutePaths";
import type { deviceProps, ILicenses, subscriptionData } from "../../types/details-api-types";
import { useTranslation } from "react-i18next";
import { useToast } from "../../hooks/useToast";
import MobileDeviceItem from "./MobileDeviceItem";
import LicenseSingleKeyModal from "../modals/LicenseSingleKeyModal/LicenseSingleKeyModal";

interface ExtendedSubscriptionData extends subscriptionData {
  licenses: ILicenses[];
}

interface MobileDeviceTableProps {
  subNumber: string;
  title?: string;
  number: string;
  data: any;
  expired?: boolean;
  trial?: boolean;
  pause?: boolean;
  canceled?: boolean;
  setIsModalManageSubVisible?: any;
  subscriptions: ExtendedSubscriptionData;
  refreshData: () => void;
  setDeactivateSuccess: (arg: boolean) => void;
}

const MobileDeviceTable: React.FC<MobileDeviceTableProps> = ({
  subNumber,
  number,
  data,
  title,
  expired,
  trial,
  pause,
  canceled,
  subscriptions,
  refreshData,
  setDeactivateSuccess,
}) => {
  const [isModalDeactiveVisible, setIsModalDeactivateVisible] = useState(false);
  const [isModalUodateVisible, setIsModalUpdateVisible] = useState(false);
  const [isModalOrderVisible, setIsModalOrderVisible] = useState(false);
  const [isModalRenewalVisible, setIsModalRenewalVisible] = useState(false);
  const [isModalSuccessfulyVisible, setIsModalSuccessfullyVisible] = useState(false);
  const [isModalProcessingVisible, setIsModalProcessingVisible] = useState(false);
  const [currentDeviceDeactivate, setCurrentDeviceDeactivate] = useState<deviceProps | null>(null);
  const [isLicenseModalVisible, setIsLicenseModalVisible] = useState(false);
  const [licenseModalData, setLicenseModalData] = useState({ key: "", name: "" });

  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showToast } = useToast();

  const mergedData = useMemo(() => {
    if (!data || !subscriptions) return { matches: 0, devices: [] };

    let matches = 0;

    const newData = data.map((device: { machine_id: any }) => {
      const matchingLicenses = subscriptions.licenses.filter((license) => license.machine_id === device.machine_id);

      // Get the first matching license_key or set to an empty string if no match
      const licenseKey = matchingLicenses.length > 0 ? matchingLicenses[0].license_key : "";

      if (matchingLicenses.length > 0) ++matches;

      return {
        ...device,
        license_key: licenseKey,
        matchingLicenses,
        hasMatch: matchingLicenses.length > 0,
      };
    });

    return {
      devices: newData,
      matches,
    };
  }, [data, subscriptions]);

  const handleLicenseKeyVisible = (isShow: boolean, key: string, name: string) => {
    setLicenseModalData({ key, name });
    setIsLicenseModalVisible(isShow);
  };

  const subscriptionExpiration = useMemo(() => {
    const expirationDate = new Date(subscriptions?.licenses?.[0]?.expiration_date).getTime();
    const today = new Date().getTime();

    // Calculate days left
    const timeDiff = expirationDate - today;
    const daysLeft = Math.max(Math.ceil(timeDiff / (1000 * 60 * 60 * 24)), 0);

    return {
      isExpired: timeDiff < 0,
      daysLeft,
    };
  }, [subscriptions]);

  const isPackageExpired = expired || subscriptionExpiration.isExpired;
  return (
    <div className={styles.root}>
      <Modal isOpen={isModalDeactiveVisible} setIsVisible={() => setIsModalDeactivateVisible(false)}>
        <DeactiveDeviceModal
          setIsModalDeactivateVisible={setIsModalDeactivateVisible}
          setDeactivateSuccess={setDeactivateSuccess}
          setDeactivateDeviceItem={setCurrentDeviceDeactivate}
          deviceItem={currentDeviceDeactivate}
        />
      </Modal>
      <Modal isOpen={isModalUodateVisible} setIsVisible={() => setIsModalUpdateVisible(false)}>
        <GetPremiumModal
          setIsModalUpdateVisible={setIsModalUpdateVisible}
          setIsModalOrderVisible={setIsModalOrderVisible}
        />
      </Modal>
      <Modal isOpen={isModalRenewalVisible} setIsVisible={() => setIsModalRenewalVisible(false)}>
        <RenewalModal
          setIsModalPauseConfirmVisible={setIsModalRenewalVisible}
          setIsModalProcessingVisible={setIsModalProcessingVisible}
          setIsModalSuccessfullyVisible={setIsModalSuccessfullyVisible}
          data={subscriptions}
        />
      </Modal>
      <Modal isOpen={isModalProcessingVisible} setIsVisible={() => setIsModalProcessingVisible(false)}>
        <ProcessingModal setIsModalProcessingVisible={setIsModalProcessingVisible} />
      </Modal>
      <Modal isOpen={isModalSuccessfulyVisible} setIsVisible={() => setIsModalSuccessfullyVisible(false)}>
        <SuccessfullyRenewalModal
          setIsModalPauseConfirmVisible={() => setIsModalSuccessfullyVisible(false)}
          refreshData={refreshData}
          data={subscriptions}
        />
      </Modal>
      <Modal isOpen={isModalOrderVisible} setIsVisible={() => setIsModalOrderVisible(false)}>
        <OrderSummaryModal />
      </Modal>
      <Modal isOpen={isLicenseModalVisible} setIsVisible={() => setIsLicenseModalVisible(false)}>
        <LicenseSingleKeyModal
          setIsLicenseModalVisible={() => setIsLicenseModalVisible(false)}
          keygen={licenseModalData.key}
          name={licenseModalData.name}
        />
      </Modal>

      {isPackageExpired ? (
        <p className={styles.root_expired}>{t("Package expired")}</p>
      ) : trial ? (
        <p className={styles.root_trial}>Expires in: Soon</p>
      ) : pause ? (
        <p className={styles.root_pause}>{t("Paused")}</p>
      ) : canceled ? (
        <p className={styles.root_expired}>
          {t("Canceled")} <br /> {t("Expires in {{num}} days", { num: subscriptionExpiration.daysLeft })}
        </p>
      ) : null}
      {!trial && <p className={styles.root_title}>{title}</p>}
      {!isPackageExpired && !trial && <p className={styles.root_number}>{number}</p>}

      {subNumber !== "0" && (
        <div className={styles.root_manage}>
          {!trial ? (
            <p className={styles.root_manage_num}>#{subNumber}</p>
          ) : (
            <p className={styles.root_title}>{title}</p>
          )}
          <p
            className={styles.root_manage_btn}
            onClick={() =>
              isPackageExpired || trial || pause
                ? setIsModalRenewalVisible(true)
                : canceled
                ? window.open("https://secure.fortect.com/", "_blank")
                : navigate(RoutePaths.ACCOUNT)
            }
          >
            {isPackageExpired || trial || canceled ? t("Renew package") : pause ? t("Renew now") : ""}
          </p>
        </div>
      )}
      {isPackageExpired ? (
        <div className={styles.root_noDevices}>{t("No active devices.")}</div>
      ) : subNumber === "0" ? (
        <div className={`${styles.root_noDevices} ${styles.root_noDevices_noTitle}`}>{t("No active devices.")}</div>
      ) : pause ? (
        <div className={styles.root_noDevices}>{t("No active devices.")}</div>
      ) : (
        // ) : canceled ? (
        //   <div className={styles.root_noDevices}>{t("No active devices.")}</div>
        mergedData.devices.length > 0 &&
        mergedData.devices.map((item: any, index: number) => (
          <MobileDeviceItem
            key={index}
            item={item}
            trial={trial}
            showToast={showToast}
            setCurrentDeviceDeactivate={setCurrentDeviceDeactivate}
            setIsModalDeactivateVisible={setIsModalDeactivateVisible}
            itemKey={item.license_key}
            setIsLicenseModalVisible={handleLicenseKeyVisible}
          />
        ))
      )}
    </div>
  );
};

export default MobileDeviceTable;
